import { forwardRef, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import {
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  useMasterData,
  useSnackbar,
} from '@ui-framework/index';
import FormWarnings from '@ui-framework/components/AdvancedForms/FormWarnings';
import { useInsightContact } from '../../components/InsightContactContext';
import { MAX_SELECTED_ROWS } from '../../lib';
import { listSchema } from '../../schemas';
import {
  phoneNumber,
  description,
  isVoiceEnabled,
  isVoiceDefault,
  isTextEnabled,
  isTextDefault,
  actions,
} from './accessorFunctions';

const STORE_NAME = 'InsightContactListStore';

const InsightContactList = forwardRef((props, ref) => {
  const { rowSelection, addRowSelection, selectedRows } = useInsightContact();
  const { formatMessage } = useLocalization();
  const masterData = useMasterData();
  const snackbar = useSnackbar();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const { commonParams, onEdit, onDelete, onRowCountChange } = props;
  const [warnings, setWarnings] = useState([]);

  const handleRowSelectionChange = useCallback(
    (rowsToAdd, updater) => {
      const { limitReached } = addRowSelection(rowsToAdd, updater);

      if (limitReached) {
        snackbar.showMessage({
          type: 'error',
          data: formatMessage(
            {
              id: 'INSIGHT_CONTACT_SELECTION_LIMIT',
              defaultMessage:
                'You can only select up to {max} contacts at a time.',
            },
            { max: MAX_SELECTED_ROWS },
          ),
        });
      }
    },
    [addRowSelection, formatMessage, snackbar],
  );

  const listProps = {
    columnPrimaryId: 'id',
    storeName: STORE_NAME,
    dataProvider,
    schema: listSchema,
    listApiUrl: commonParams.phonesUrl,
    initialState: { showColumnFilters: false },
    sortBy: 'phoneNumber',
    sortOrder: 'ASC',
    ref,
    accessorFunctions: {
      phoneNumber,
      description,
      isVoiceEnabled,
      isVoiceDefault,
      isTextEnabled,
      isTextDefault,
      actions,
    },
    accessorFunctionsAdditional: {
      formatMessage,
      saPhoneNumber: masterData?.saPhoneNumber,
      insightPurchased: commonParams.insightPurchased,
      onEdit,
      onDelete,
    },
    onRowCountChange,
    onApiResponse: apiResponse => {
      if (apiResponse?.ok && apiResponse.data?.warnings) {
        setWarnings(apiResponse.data?.warnings);
      }
    },
    ...(commonParams.canWrite && {
      rowSelectionProps: {
        rowSelection,
        onRowSelection: handleRowSelectionChange,
        rowSelectionStyleProps: {
          muiSelectAllCheckboxProps: ({ table }) => ({
            indeterminate:
              selectedRows.length > 0 && !table.getIsAllPageRowsSelected(),
          }),
        },
      },
    }),
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box sx={{ pt: warnings.length > 0 ? 1.5 : 0 }}>
        <FormWarnings warnings={warnings} />
      </Box>
      <CRUDComponent
        type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
        listProps={listProps}
      />
    </Box>
  );
});

export default InsightContactList;
